import React, { useEffect, useState } from 'react';
import { FaArrowRight, FaExclamationTriangle, FaSpinner } from 'react-icons/fa';

const API_BASE = process.env.REACT_APP_API_URL || '';

/**
 * Compact owner/manager sell + market visibility:
 * funnel strip, project health, aging exceptions — across all org units.
 */
export default function SellMarketOps({ language = 'ar', onNavigate }) {
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(`${API_BASE}/api/ops/visibility`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!res.ok) throw new Error(await res.text());
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const goUnits = (hint) => {
    try {
      if (hint) sessionStorage.setItem('units_ops_filter', hint);
    } catch (_) { /* ignore */ }
    onNavigate?.('units');
  };

  const goDeals = () => onNavigate?.('deals');

  const funnel = data?.funnel || {};
  const inventory = data?.inventory || {};
  const projects = data?.projects || [];
  const aging = data?.aging || {};

  const stages = [
    {
      key: 'available_no_ad',
      label: t('متاحة بلا إعلان', 'Available · no ad'),
      value: funnel.available_no_ad || 0,
      onClick: () => goUnits('needs_ad'),
      tone: (funnel.available_no_ad || 0) > 0 ? 'warn' : 'ok',
    },
    {
      key: 'assigned_pending_ad',
      label: t('بانتظار النشر', 'Ad pending'),
      value: funnel.assigned_pending_ad || 0,
      onClick: () => goUnits('pending_ad'),
      tone: 'neutral',
    },
    {
      key: 'live_ad',
      label: t('إعلان منشور', 'Live ad'),
      value: funnel.live_ad || 0,
      onClick: () => goUnits('live_ad'),
      tone: 'ok',
    },
    {
      key: 'reserved',
      label: t('محجوزة', 'Reserved'),
      value: funnel.reserved || 0,
      onClick: () => goUnits('reserved'),
      tone: 'neutral',
    },
    {
      key: 'active_deals',
      label: t('صفقات نشطة', 'Active deals'),
      value: funnel.active_deals || 0,
      onClick: goDeals,
      tone: 'neutral',
    },
    {
      key: 'closed_units',
      label: t('مباعة / مؤجرة', 'Sold / rented'),
      value: funnel.closed_units || 0,
      onClick: () => goUnits('closed'),
      tone: 'ok',
    },
  ];

  const agingItems = [
    ...(aging.vacant_no_ad || []).map((u) => ({
      id: `v-${u.id}`,
      kind: t('بلا إعلان', 'No ad'),
      title: u.unit_number,
      sub: u.context_name,
      days: u.days_open,
      onClick: () => goUnits('needs_ad'),
    })),
    ...(aging.reserved_stuck || []).map((u) => ({
      id: `r-${u.id}`,
      kind: t('محجوزة طويلاً', 'Stuck reserved'),
      title: u.unit_number,
      sub: u.context_name,
      days: u.days_open,
      onClick: () => goUnits('reserved'),
    })),
    ...(aging.ads_overdue || []).map((l) => ({
      id: `a-${l.id}`,
      kind: t('إعلان متأخر', 'Ad overdue'),
      title: l.unit_number || `#${l.id}`,
      sub: l.platform,
      days: l.days_open,
      onClick: () => goUnits('pending_ad'),
    })),
    ...(aging.deals_stale || []).map((d) => ({
      id: `d-${d.id}`,
      kind: t('صفقة راكدة', 'Stale deal'),
      title: d.property_address || `#${d.id}`,
      sub: d.deal_type,
      days: d.days_stale,
      onClick: goDeals,
    })),
  ].slice(0, 8);

  return (
    <section
      style={{
        background: '#fff',
        borderRadius: 14,
        border: '1px solid #fde5d4',
        marginBottom: 20,
        overflow: 'hidden',
        boxShadow: '0 2px 12px rgba(249, 115, 22, 0.05)',
      }}
      dir={isAr ? 'rtl' : 'ltr'}
    >
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          padding: '14px 18px',
          background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
          borderBottom: '1px solid #fed7aa',
        }}
      >
        <div>
          <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#9a3412', fontWeight: 700 }}>
            {t('البيع والتسويق', 'Sell & market')}
            {loading && (
              <FaSpinner style={{ marginInlineStart: 8, animation: 'spin 1s linear infinite', fontSize: 12 }} />
            )}
          </h3>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#9a3412', opacity: 0.85 }}>
            {t(
              'ملخص لكل الوحدات في حسابك — من المخزون إلى الصفقة. اضغط أي مرحلة.',
              'Summary across all units in your account — inventory to deal. Tap any stage.'
            )}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {inventory.total > 0 && (
            <span style={{ fontSize: 12, color: '#9a3412', fontWeight: 600 }}>
              {inventory.sold_pct}% {t('مباعة', 'sold')} · {inventory.rented_pct}% {t('مؤجرة', 'rented')}
            </span>
          )}
          <button
            type="button"
            onClick={() => onNavigate?.('units')}
            style={{
              padding: '7px 12px',
              background: '#fff',
              color: '#ea580c',
              border: '1px solid #fed7aa',
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 500,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            {t('الوحدات', 'Units')}
            <FaArrowRight style={{ fontSize: 10 }} />
          </button>
        </div>
      </header>

      <div style={{ padding: '16px 18px 18px' }}>
        {error && (
          <div style={{ marginBottom: 12, padding: 10, background: '#fef2f2', color: '#b91c1c', borderRadius: 8, fontSize: 13 }}>
            {t('تعذر تحميل لوحة البيع والتسويق', 'Could not load sell & market panel')}
          </div>
        )}

        {/* Funnel */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
            gap: 8,
            marginBottom: 16,
          }}
        >
          {stages.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={s.onClick}
              style={{
                textAlign: isAr ? 'right' : 'left',
                padding: '12px 10px',
                borderRadius: 10,
                border: `1px solid ${s.tone === 'warn' ? '#fdba74' : '#e5e7eb'}`,
                background: s.tone === 'warn' ? '#fff7ed' : '#fafafa',
                cursor: 'pointer',
              }}
            >
              <div style={{ fontSize: 22, fontWeight: 800, color: '#111827', lineHeight: 1.1 }}>{s.value}</div>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4, fontWeight: 600, lineHeight: 1.3 }}>
                {s.label}
              </div>
            </button>
          ))}
        </div>

        {/* Projects + Aging */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: 14,
          }}
        >
          <div style={{ border: '1px solid #f1f5f9', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '10px 12px', background: '#f8fafc', fontSize: 12, fontWeight: 700, color: '#475569' }}>
              {t('صحة المشاريع', 'Project health')}
            </div>
            {projects.length === 0 ? (
              <p style={{ margin: 0, padding: 14, fontSize: 13, color: '#94a3b8' }}>
                {t('لا مشاريع بعد', 'No projects yet')}
              </p>
            ) : (
              <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ color: '#94a3b8', textAlign: isAr ? 'right' : 'left' }}>
                      <th style={{ padding: '8px 10px', fontWeight: 600 }}>{t('المشروع', 'Project')}</th>
                      <th style={{ padding: '8px 6px', fontWeight: 600 }}>{t('متاح', 'Open')}</th>
                      <th style={{ padding: '8px 6px', fontWeight: 600 }}>%{t('بيع', 'Sold')}</th>
                      <th style={{ padding: '8px 6px', fontWeight: 600 }}>{t('بلا إعلان', 'No ad')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map((p) => (
                      <tr key={`${p.project_id}-${p.project_name}`} style={{ borderTop: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '8px 10px', fontWeight: 600, color: '#1e293b' }}>
                          {p.project_name}
                          <div style={{ fontWeight: 400, color: '#94a3b8', fontSize: 11 }}>
                            {p.total} {t('وحدة', 'units')}
                          </div>
                        </td>
                        <td style={{ padding: '8px 6px' }}>{p.vacant}</td>
                        <td style={{ padding: '8px 6px' }}>{p.sold_pct}%</td>
                        <td style={{ padding: '8px 6px', color: p.no_ad > 0 ? '#c2410c' : '#64748b', fontWeight: p.no_ad > 0 ? 700 : 400 }}>
                          {p.no_ad}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div style={{ border: '1px solid #f1f5f9', borderRadius: 12, overflow: 'hidden' }}>
            <div
              style={{
                padding: '10px 12px',
                background: '#fff7ed',
                fontSize: 12,
                fontWeight: 700,
                color: '#9a3412',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <FaExclamationTriangle />
              {t('عالق / يحتاج إجراء', 'Stuck / needs action')}
            </div>
            {agingItems.length === 0 ? (
              <p style={{ margin: 0, padding: 14, fontSize: 13, color: '#94a3b8' }}>
                {t('لا عناصر متأخرة الآن', 'Nothing aging right now')}
              </p>
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, maxHeight: 220, overflowY: 'auto' }}>
                {agingItems.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={item.onClick}
                      style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 8,
                        padding: '10px 12px',
                        border: 'none',
                        borderBottom: '1px solid #f1f5f9',
                        background: '#fff',
                        cursor: 'pointer',
                        textAlign: isAr ? 'right' : 'left',
                      }}
                    >
                      <span>
                        <span style={{ display: 'block', fontSize: 11, color: '#ea580c', fontWeight: 700 }}>{item.kind}</span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{item.title}</span>
                        {item.sub && (
                          <span style={{ display: 'block', fontSize: 11, color: '#94a3b8' }}>{item.sub}</span>
                        )}
                      </span>
                      <span style={{ fontSize: 12, color: '#b45309', fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {item.days != null ? `${item.days}d` : '—'}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
